import React, { FC, useContext } from "react";
import { ClickAwayListener, Collapse, Divider, Grid, Paper } from "@material-ui/core";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import { DataContext, VisibilityContext } from "../../context";
import { FILTERS } from "../../constants";
import { ReactComponent as PhaseSVG } from "../../svgs/phase-icon.svg";
import { ReactComponent as OtherAttrSVG } from "../../svgs/other-attributes.svg";
import LaunchIcon from "../../icons/launch-icon.png";
import DrugCharacteristicsIcon from "../../icons/drug-characteristics.png";
import DatePickers from "./DatePickers";
import Autocompletes from "./Autocompletes";
import Checkboxes from "./Checkboxes";
import Header from "../Common/Header";
import Radios from "./Radios";
import { OPTIONS } from "../../constants/filters";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      backgroundColor: theme.palette.background.default,
      width: 355,
    },
    section: {
      padding: 12,
    },
    divider: {
      height: 2,
      backgroundColor: theme.palette.grey[400],
    },
    icon: {
      height: "18px",
      marginRight: "8px",
    },
  })
);

interface FilterMenuProps {}

const FilterMenu: FC<FilterMenuProps> = (props: FilterMenuProps) => {
  const classes = useStyles();

  const [, , complexFormTypeDescriptions] = useContext(DataContext);
  const [visibilityContext, setVisibilityContext] = useContext(VisibilityContext);

  const handleClickAway = () => {
    setVisibilityContext({
      ...visibilityContext,
      menu: false,
    });
  };

  // organize nested checkbox tooltips by { [parent]: { [child]: text } }; add a { [parent]: { [parent]: text } } if you want one at the top level as well
  const cbComplexFormTooltips = OPTIONS.complexForms.reduce(
    (pAcc, p) => ({
      ...pAcc,
      [p.name]: p.values.reduce(
        (cAcc, c) => ({ ...cAcc, [c]: complexFormTypeDescriptions.find((f) => f.name === c)?.description }),
        {}
      ),
    }),
    {}
  );
  // organize autocomplete tooltips by { [MENU_AUTOCOMPLETES_TITLE]: { [option]: text } }
  const acComplexFormTooltips = {
    complexForms: OPTIONS.complexForms
      .flatMap((x) => x.values)
      .reduce((acc, x) => ({ ...acc, [x]: complexFormTypeDescriptions.find((f) => f.name === x)?.description }), {}),
  };
  return (
    <Collapse in={visibilityContext.menu} mountOnEnter unmountOnExit>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Paper elevation={4} className={classes.paper}>
          <Grid container>
            <Grid className={classes.section} item xs={12}>
              <Header
                title={FILTERS.MENU_SECTION_TITLES.stages}
                icon={<PhaseSVG className={classes.icon} />}
                variant="h4"
                isCollapsible
              />
              <Checkboxes section="stages" visibilitySection="stages" />
            </Grid>
            <Grid className={classes.section} item xs={12}>
              <Divider className={classes.divider} />
            </Grid>
            <Grid className={classes.section} item xs={12}>
              <Header
                title={FILTERS.MENU_SECTION_TITLES.launchCharacteristics}
                icon={<img src={LaunchIcon} className={classes.icon} />}
                variant="h4"
                isCollapsible
              />
              <DatePickers
                visibilitySection="launchCharacteristics"
                label={FILTERS.MENU_SECTION_OPTIONS_LABELS.pdufa}
              />
              <Checkboxes
                section="submissionTypes"
                visibilitySection="launchCharacteristics"
                label={FILTERS.MENU_SECTION_OPTIONS_LABELS.submissionTypes}
              />
              <Checkboxes
                section="fdaDesignations"
                visibilitySection="launchCharacteristics"
                label={FILTERS.MENU_SECTION_OPTIONS_LABELS.fdaDesignations}
              />
            </Grid>
            <Grid className={classes.section} item xs={12}>
              <Divider className={classes.divider} />
            </Grid>
            <Grid className={classes.section} item xs={12}>
              <Header
                title={FILTERS.MENU_SECTION_TITLES.drugCharacteristics}
                icon={<img src={DrugCharacteristicsIcon} className={classes.icon} />}
                variant="h4"
                isCollapsible
              />
              <Radios
                section="drugTypes"
                label={FILTERS.MENU_SECTION_OPTIONS_LABELS.drugTypes}
                visibilitySection="drugCharacteristics"
              />
              <Autocompletes visibilitySection="drugCharacteristics" tooltips={acComplexFormTooltips} />
            </Grid>
            <Grid className={classes.section} item xs={12}>
              <Divider className={classes.divider} />
            </Grid>
            <Grid className={classes.section} item xs={12}>
              <Header
                title={FILTERS.MENU_SECTION_TITLES.otherAttributes}
                icon={<OtherAttrSVG className={classes.icon} />}
                variant="h4"
                isCollapsible
              />
              <Checkboxes section="otherAttributes" visibilitySection="otherAttributes" />
            </Grid>
          </Grid>
        </Paper>
      </ClickAwayListener>
    </Collapse>
  );
};

export default FilterMenu;
